import React, { useRef } from 'react';
import { CheckCircle, AlertCircle, Target, Calendar, User, Brain, Heart, Utensils, Home, Download, FileText } from 'lucide-react';

const ICONS = {
  Heart: <Heart className="w-5 h-5" />,
  User: <User className="w-5 h-5" />,
  Brain: <Brain className="w-5 h-5" />,
  Utensils: <Utensils className="w-5 h-5" />,
  Home: <Home className="w-5 h-5" />
};

const ProgressCard = ({ area, data }) => (
  <div className="bg-white rounded-lg shadow-md p-6 mb-4">
    <div className="flex items-center mb-4">
      <div className="text-brand-600 mr-3">{ICONS[data.icon] || null}</div>
      <h3 className="text-lg font-semibold text-gray-800">{data.title}</h3>
    </div>
    <div className="grid md:grid-cols-3 gap-4">
      <div>
        <h4 className="flex items-center text-sm font-medium text-green-700 mb-2">
          <CheckCircle className="w-4 h-4 mr-1" /> Avanços
        </h4>
        <ul className="space-y-1">
          {data.advances.map((advance, idx) => (
            <li key={idx} className="text-sm text-gray-600 flex items-start">
              <span className="w-2 h-2 bg-green-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              {advance}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="flex items-center text-sm font-medium text-amber-700 mb-2">
          <AlertCircle className="w-4 h-4 mr-1" /> Desafios
        </h4>
        <ul className="space-y-1">
          {data.challenges.map((challenge, idx) => (
            <li key={idx} className="text-sm text-gray-600 flex items-start">
              <span className="w-2 h-2 bg-amber-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              {challenge}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="flex items-center text-sm font-medium text-purple-700 mb-2">
          <Target className="w-4 h-4 mr-1" /> Objetivos (6 meses)
        </h4>
        <ul className="space-y-1">
          {data.futureGoals.map((goal, idx) => (
            <li key={idx} className="text-sm text-gray-600 flex items-start">
              <span className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              {goal}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const ObservationAnalysisDashboard = ({ dashboard, patientName }) => {
  const dashboardRef = useRef();
  
  // PDF Export Function
  const exportToPDF = async () => {
    try {
      setExportLoading(true);
      
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      const date = new Date().toISOString().split('T')[0];
      const filename = `Painel_Progresso_Terapeutico_${(patientName || 'Paciente').replace(/\s+/g, '_')}_${date}.pdf`;
      
      // Fix width so PDF looks the same regardless of screen/window size
      const el = dashboardRef.current;
      const originalWidth = el.style.width;
      el.style.width = '1024px';

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: el.scrollHeight,
        width: 1024
      });

      el.style.width = originalWidth;
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const margin = 10;
      const ratio = (pdfWidth - 2 * margin) / imgWidth;

      const scaledWidth = pdfWidth - 2 * margin;
      const scaledHeight = imgHeight * ratio;

      const x = margin;
      const y = 10;
      
      if (scaledHeight > pdfHeight - 20) {
        // Multi-page handling
        const pageHeight = pdfHeight - 20;
        let currentY = 0;
        
        while (currentY < scaledHeight) {
          if (currentY > 0) {
            pdf.addPage();
          }
          
          const currentPageHeight = Math.min(pageHeight, scaledHeight - currentY);
          const sourceY = (currentY / ratio);
          const sourceHeight = (currentPageHeight / ratio);
          
          // Create temporary canvas for this page
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = imgWidth;
          tempCanvas.height = sourceHeight;
          const tempCtx = tempCanvas.getContext('2d');
          
          tempCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
          const tempImgData = tempCanvas.toDataURL('image/png');
          
          pdf.addImage(tempImgData, 'PNG', x, y, scaledWidth, currentPageHeight);
          currentY += currentPageHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }
      
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setExportLoading(false);
    }
  };

  const [exportLoading, setExportLoading] = React.useState(false);
  const [exportWordLoading, setExportWordLoading] = React.useState(false);

  // Word Export Function
  const exportToWord = async () => {
    try {
      setExportWordLoading(true);

      const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } = await import('docx');
      const fileSaver = await import('file-saver');
      const saveAs = fileSaver.default || fileSaver.saveAs;

      const date = new Date().toISOString().split('T')[0];
      const filename = `Painel_Progresso_Terapeutico_${(patientName || 'Paciente').replace(/\s+/g, '_')}_${date}.docx`;

      const children = [];

      // Title
      children.push(
        new Paragraph({
          text: 'Painel de Progresso Terapêutico',
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
        })
      );

      // Patient name
      if (patientName) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Paciente: ', bold: true }),
              new TextRun({ text: patientName }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
          })
        );
      }

      // Date
      children.push(
        new Paragraph({
          text: new Date().toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Areas
      Object.entries(areas).forEach(([area, data]) => {
        // Area title
        children.push(
          new Paragraph({
            text: data.title,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        // Advances
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '✓ Avanços', bold: true, color: '16a34a' }),
            ],
            spacing: { before: 200, after: 100 },
          })
        );
        data.advances.forEach(advance => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `• ${advance}` }),
              ],
              indent: { left: 720 },
            })
          );
        });

        // Challenges
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '⚠ Desafios', bold: true, color: 'd97706' }),
            ],
            spacing: { before: 200, after: 100 },
          })
        );
        data.challenges.forEach(challenge => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `• ${challenge}` }),
              ],
              indent: { left: 720 },
            })
          );
        });

        // Future Goals
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '🎯 Objetivos (6 meses)', bold: true, color: '9333ea' }),
            ],
            spacing: { before: 200, after: 100 },
          })
        );
        data.futureGoals.forEach(goal => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `• ${goal}` }),
              ],
              indent: { left: 720 },
            })
          );
        });
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, filename);

    } catch (error) {
      console.error('Error exporting Word:', error);
      alert('Erro ao exportar Word. Tente novamente.');
    } finally {
      setExportWordLoading(false);
    }
  };
  
  if (!dashboard || !dashboard.areas) return <div className="text-gray-500">Nenhuma análise disponível.</div>;
  const areas = dashboard.areas;
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Painel de Progresso Terapêutico</h2>
        <div className="flex space-x-2">
          <button
            onClick={exportToPDF}
            disabled={exportLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span>
              {exportLoading ? 'Exportando...' : 'Exportar PDF'}
            </span>
          </button>
          <button
            onClick={exportToWord}
            disabled={exportWordLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4" />
            <span>
              {exportWordLoading ? 'Exportando...' : 'Exportar Word'}
            </span>
          </button>
        </div>
      </div>
      
      <div ref={dashboardRef}>
        {/* Patient info header for PDF */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 text-center">
            Painel de Progresso Terapêutico
          </h3>
          {patientName && (
            <p className="text-center text-gray-600 mt-1">
              <span className="font-medium">Paciente:</span> {patientName}
            </p>
          )}
          <p className="text-center text-gray-500 text-sm mt-1">
            {new Date().toLocaleDateString('pt-BR', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        
        {Object.keys(areas).length === 0 && <div className="text-gray-500">Nenhuma área identificada na análise.</div>}
        {Object.entries(areas).map(([area, data]) => (
          <React.Fragment key={area}>
            <ProgressCard area={area} data={data} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ObservationAnalysisDashboard; 